/**
 * Signature policy is an account level setting that decides whether the agent
 * signature is appended to outgoing messages, and whether the agent is allowed
 * to change that.
 *
 * hidden       -> never signs, the toggle is not rendered
 * optional_off -> agent decides, starts off (default, matches legacy behaviour)
 * optional_on  -> agent decides, starts on
 * enforced     -> always signs, the toggle is not rendered
 */
export const SIGNATURE_POLICIES = {
  HIDDEN: 'hidden',
  OPTIONAL_OFF: 'optional_off',
  OPTIONAL_ON: 'optional_on',
  ENFORCED: 'enforced',
};

const VALID_POLICIES = Object.values(SIGNATURE_POLICIES);

/**
 * Reads the policy from the account, falling back to the legacy behaviour when
 * the account has no policy set or has an unknown value.
 * @param {Object} account - The account object.
 * @returns {string} One of SIGNATURE_POLICIES.
 */
export function getSignaturePolicy(account) {
  const policy = account?.settings?.agent_signature_policy;
  return VALID_POLICIES.includes(policy)
    ? policy
    : SIGNATURE_POLICIES.OPTIONAL_OFF;
}

/**
 * Whether the agent can turn the signature on and off for this account.
 * @param {string} policy - One of SIGNATURE_POLICIES.
 * @returns {boolean}
 */
export function canAgentToggleSignature(policy) {
  return (
    policy === SIGNATURE_POLICIES.OPTIONAL_OFF ||
    policy === SIGNATURE_POLICIES.OPTIONAL_ON
  );
}

/**
 * Resolves whether the signature should be appended, combining the account
 * policy with the agent's own per channel preference. The preference only wins
 * on the optional policies; an agent who never touched the toggle has no value
 * stored, and that is when the policy default applies.
 * @param {string} policy - One of SIGNATURE_POLICIES.
 * @param {boolean|undefined} agentPreference - The agent's UI setting for the channel.
 * @returns {boolean}
 */
export function isSignatureEnabled(policy, agentPreference) {
  if (policy === SIGNATURE_POLICIES.ENFORCED) return true;
  if (policy === SIGNATURE_POLICIES.HIDDEN) return false;
  if (agentPreference === undefined || agentPreference === null) {
    return policy === SIGNATURE_POLICIES.OPTIONAL_ON;
  }
  return !!agentPreference;
}
